import * as React from 'react'

import { ButtonProps, FocusableRef, ScopedCSSRules } from '@dna/react-types'
import { buttonStyles, css, cx } from '@dna/core'
import { mergeProps } from '@react-aria/utils'
import { useButton } from '@react-aria/button'
import { useFocusRing } from '@react-aria/focus'
import { useFocusableRef } from '@react-spectrum/utils'
import { useHover } from '@react-aria/interactions'
import { useStyleProps } from '../utils'

function Button(props: ButtonProps, ref: FocusableRef) {
  let {
    UNSAFE_className,
    UNSAFE_style,
    as: ElementType = 'button',
    children,
    css: styleProps = {},
    endIcon: EndIcon,
    isDisabled,
    kind = 'secondary',
    onBackground,
    startIcon: StartIcon,
    ...otherProps
  } = props

  let domRef = useFocusableRef(ref)
  let { buttonProps } = useButton(props, domRef)
  let { hoverProps } = useHover({ isDisabled })
  let { isFocusVisible, focusProps } = useFocusRing()
  let styles = buttonStyles({
    options: { isFocusVisible, kind, onBackground },
  })
  let {
    styleProps: { className, style },
  } = useStyleProps({ ...styleProps, UNSAFE_className, UNSAFE_style })

  return (
    <ElementType
      {...otherProps}
      {...mergeProps(buttonProps, hoverProps, focusProps)}
      ref={domRef}
      className={cx(
        css(styles as ScopedCSSRules),
        css(style as ScopedCSSRules),
        className
      )}
    >
      {StartIcon && <StartIcon />}
      {children}
      {EndIcon && <EndIcon />}
    </ElementType>
  )
}

/**
 * Primary buttons are used mostly for actions, such as “Create”, "Submit", or "Save".
 * Secondary and tertiary buttons are used for less important or less commonly used actions, such as “Close”.'
 *
 * @name
 * Button
 *
 * @see
 * https://dna.telstrahealth.com.au/components/button
 *
 * @example
 * <Button kind="secondary">Close</Button>
 */
let _Button = React.forwardRef(Button)
export { _Button as Button }
